import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  Grid,
  InputBase,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { saveApiData } from '../store/Slice/Global/GlobalSlice';
import { loginApi } from '../utils/Apis';
import { isValidValue } from '../utils/CommonFunction';

function UpdatePassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loginMailId = sessionStorage.getItem('loginMail');

  // Form state
  const [formData, setFormData] = useState({
    username: loginMailId,
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!isValidValue(formData.username)) {
      newErrors.username = 'Email is required';
    } else {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.username)) {
        newErrors.username = 'Please enter a valid email address';
      }
    }

    // Old password validation
    if (!isValidValue(formData.oldPassword)) {
      newErrors.oldPassword = 'Current password is required';
    } else if (formData.oldPassword.length < 6) {
      newErrors.oldPassword = 'Password must be at least 6 characters';
    }

    // New password validation
    if (!isValidValue(formData.newPassword)) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters';
    }

    // Confirm password validation
    if (!isValidValue(formData.confirmPassword)) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Check if new password is same as old password
    if (formData.oldPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle update password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setSuccessMessage('');
    setErrorMessage('');

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const finalData = {
        username: formData.username.trim(),
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      };

      // Dispatch API call
      const res = await dispatch(saveApiData(loginApi?.updatePassword, finalData));
      // console.log('Update password response:', res);

      if (res?.status === 'success' || res?.payload?.status === 'success') {
        setSuccessMessage('Password updated successfully. Redirecting to login...');
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      } else {
        setErrorMessage(res?.message || res?.payload?.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Update password error:', error);
      setErrorMessage(error?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Clear messages
  const clearMessage = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  return (
    <Box className="LoginOuter" sx={{ width: '100%', minHeight: '100vh' }}>
      <Container maxWidth="fullWidth" disableGutters>
        <Stack>
          <Grid
            container
            className="loginInner"
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item md={6} container justifyContent="center" alignContent="center">
              <Grid item xs={11} sm={9} md={7}>
                <Box>
                  <img
                    src="../../static/logos/saishlogo.jpg"
                    alt="Saish Logo"
                    className="loginLogo"
                  />
                </Box>
                <Typography variant="h4" className="loginTitle">
                  Loan Management System
                </Typography>
                <Typography variant="body2" className="loginSubTitle">
                  by Kalolytic Solutions
                </Typography>
                <Typography variant="h5" className="mb-3">
                  Update Password
                </Typography>

                {/* Success Message */}
                {successMessage && (
                  <Alert severity="success" onClose={clearMessage} sx={{ mb: 2 }}>
                    {successMessage}
                  </Alert>
                )}

                {/* Error Message */}
                {errorMessage && (
                  <Alert
                    severity="error"
                    action={
                      <IconButton size="small" color="inherit" onClick={clearMessage}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{ mb: 2 }}
                  >
                    {errorMessage}
                  </Alert>
                )}

                <form onSubmit={handleUpdatePassword}>
                  {/* Email Input */}
                  <FormControl
                    fullWidth
                    variant="standard"
                    className="formGroupLogin mb-2"
                    error={!!errors.username}
                  >
                    <MailOutlineIcon className="formControlIcon" />
                    <InputBase
                      name="username"
                      type="email"
                      placeholder="Email Address"
                      value={formData.username}
                      onChange={handleInputChange}
                      inputProps={{
                        'aria-label': 'email address',
                        className: 'formControlFull formControlFullE'
                      }}
                      disabled
                    />
                  </FormControl>
                  {errors.username && (
                    <Typography variant="caption" sx={{ color: 'red', display: 'block', mb: 1 }}>
                      {errors.username}
                    </Typography>
                  )}

                  {/* Old Password Input */}
                  <FormControl
                    fullWidth
                    variant="standard"
                    className="formGroupLogin mb-2"
                    error={!!errors.oldPassword}
                  >
                    <LockOpenIcon className="formControlIcon" />
                    <InputBase
                      name="oldPassword"
                      type="password"
                      placeholder="Current Password"
                      value={formData.oldPassword}
                      onChange={handleInputChange}
                      inputProps={{
                        'aria-label': 'current password',
                        className: 'formControlFull formControlFullP'
                      }}
                    />
                  </FormControl>
                  {errors.oldPassword && (
                    <Typography variant="caption" sx={{ color: 'red', display: 'block', mb: 1 }}>
                      {errors.oldPassword}
                    </Typography>
                  )}

                  {/* New Password Input */}
                  <FormControl
                    fullWidth
                    variant="standard"
                    className="formGroupLogin mb-2"
                    error={!!errors.newPassword}
                  >
                    <LockOpenIcon className="formControlIcon" />
                    <InputBase
                      name="newPassword"
                      type="password"
                      placeholder="New Password"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      inputProps={{
                        'aria-label': 'new password',
                        className: 'formControlFull formControlFullP'
                      }}
                    />
                  </FormControl>
                  {errors.newPassword && (
                    <Typography variant="caption" sx={{ color: 'red', display: 'block', mb: 1 }}>
                      {errors.newPassword}
                    </Typography>
                  )}

                  {/* Confirm Password Input */}
                  <FormControl
                    fullWidth
                    variant="standard"
                    className="formGroupLogin mb-2"
                    error={!!errors.confirmPassword}
                  >
                    <LockOpenIcon className="formControlIcon" />
                    <InputBase
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm New Password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      inputProps={{
                        'aria-label': 'confirm password',
                        className: 'formControlFull formControlFullP'
                      }}
                    />
                  </FormControl>
                  {errors.confirmPassword && (
                    <Typography variant="caption" sx={{ color: 'red', display: 'block', mb: 1 }}>
                      {errors.confirmPassword}
                    </Typography>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    className="loginBtn mb-2"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Update Password'}
                  </Button>
                </form>
              </Grid>
            </Grid>

            {/* Right Banner */}
            <Grid container item md={6}>
              <Box className="loginBanner">
                <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                  <Grid item sm={11} md={10} lg={10}>
                    <Box>
                      <img
                        src="../../static/login/personImage.png"
                        alt="Person"
                        className="loginRightImg"
                      />
                    </Box>
                    <Divider className="mb-2" />
                    <Typography variant="subtitle1" className="loginRightText">
                      The Kpros of Prosperity and Profits brought to you by
                    </Typography>
                    <Typography variant="h4" className="loginRightTextB">
                      Kalolytic Solutions
                    </Typography>
                    <Divider className="mt-2" />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}

export default UpdatePassword;
